import { type FC, type FormEvent, useMemo, useState } from "react";

import { grantOrganizationCreditsAsAdmin } from "../../dashboard-client";
import { formatUsd } from "../../utils/currency";
import { Alert } from "../common/Alert";
import { Button } from "../common/Button";
import { FormInput } from "../common/forms/FormInput";
import { FormTextarea } from "../common/forms/FormTextarea";
import { Text } from "../common/Text";

const QUICK_GRANT_AMOUNTS = [25, 100, 250] as const;

function validateAmount(
  amountUsd: string,
  parsedAmount: number,
): string | null {
  if (amountUsd.trim().length === 0) {
    return "Amount is required.";
  }
  if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
    return "Amount must be a positive number.";
  }
  return null;
}

interface AdminOrgCreditsPanelProps {
  orgId: string;
  currentBalanceUsd: number;
  onGranted: () => Promise<void>;
}

export const AdminOrgCreditsPanel: FC<AdminOrgCreditsPanelProps> = ({
  orgId,
  currentBalanceUsd,
  onGranted,
}) => {
  const [amountUsd, setAmountUsd] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [expiresAtLocal, setExpiresAtLocal] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const parsedAmount = useMemo(() => Number.parseFloat(amountUsd), [amountUsd]);
  const amountError = validateAmount(amountUsd, parsedAmount);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    if (amountError) {
      setError(amountError);
      return;
    }

    const expiresAtIso =
      expiresAtLocal.trim().length > 0
        ? new Date(expiresAtLocal).toISOString()
        : undefined;

    if (expiresAtIso && Number.isNaN(new Date(expiresAtIso).getTime())) {
      setError("Expiration must be a valid date/time.");
      return;
    }

    setSubmitting(true);
    try {
      const { data: result } = await grantOrganizationCreditsAsAdmin({
        body: {
          amountUsd: parsedAmount,
          description:
            description.trim().length > 0 ? description.trim() : undefined,
          expiresAt: expiresAtIso,
        },
        path: { organizationId: orgId },
      });
      await onGranted();
      setSuccess(
        `Granted ${formatUsd(result.amountUsd)}. New balance: ${formatUsd(result.balanceAfterUsd)}.`,
      );
      setAmountUsd("");
      setDescription("");
      setExpiresAtLocal("");
    } catch (caughtError) {
      setError(
        caughtError instanceof Error
          ? caughtError.message
          : "Failed to grant credits.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="rounded-none border border-slate-300 bg-slate-50/60 p-4 dark:border-slate-700 dark:bg-slate-900/40">
      <div className="mb-3 flex items-center justify-between">
        <h4 className="font-mono text-[11px] font-bold uppercase tracking-widest text-slate-900 dark:text-white">
          Grant Credits
        </h4>
        <Text>
          Current:{" "}
          <span className="font-mono text-gray-700 dark:text-gray-200">
            {formatUsd(currentBalanceUsd)}
          </span>
        </Text>
      </div>

      {error && (
        <Alert variant="error" size="sm" className="mb-3">
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" size="sm" className="mb-3">
          {success}
        </Alert>
      )}

      <form
        className="space-y-3"
        onSubmit={(event) => void handleSubmit(event)}
      >
        <div className="flex flex-wrap gap-2">
          {QUICK_GRANT_AMOUNTS.map((amount) => (
            <Button
              key={amount}
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setAmountUsd(String(amount))}
            >
              +{formatUsd(amount)}
            </Button>
          ))}
        </div>

        <FormInput
          label="Amount (USD)"
          type="number"
          min={0.01}
          step="0.01"
          value={amountUsd}
          onChange={(event) => setAmountUsd(event.currentTarget.value)}
          placeholder="100.00"
          error={error && amountError ? amountError : undefined}
        />

        <FormInput
          label="Expires At (Optional)"
          type="datetime-local"
          value={expiresAtLocal}
          onChange={(event) => setExpiresAtLocal(event.currentTarget.value)}
        />

        <FormTextarea
          label="Description (Optional)"
          rows={2}
          value={description}
          onChange={(event) => setDescription(event.currentTarget.value)}
          placeholder="Reason for this credit grant"
        />

        <Button
          type="submit"
          variant="primary"
          loading={submitting}
          loadingText="Granting..."
          disabled={Boolean(amountError)}
        >
          Grant Credits
        </Button>
      </form>
    </div>
  );
};
